/* eslint-disable @typescript-eslint/naming-convention */
import type {FetchWrapper} from '@/ts/api/fetch-wrapper';
import type {Language} from '@/ts/types/component/localization.type';
import type {ZitadelGetTokenResponse} from '@/ts/types/dto/zitadel-dto';
import type {ZitadelConfig} from '@/ts/utils/consts';

/**
 * {@link https://zitadel.com/docs/apis/openidoauth/endpoints|Zitadel API}
 */
export class ZitadelApi {
  public constructor(private readonly fetch: FetchWrapper) {
  }

  public redirectToSignIn(
    redirectUri: string,
    codeChallenge: string,
    zitadelConfig: ZitadelConfig,
    options: {lang: Language; loginHint?: string | null; idpId?: string | null},
  ): void {
    const scopes = [
      'openid',
      'profile',
      'email',
      'offline_access',
      `urn:zitadel:iam:org:id:${zitadelConfig.organization_resource_id}`,
      `urn:zitadel:iam:org:project:id:${zitadelConfig.project_resource_id}:aud`,
      'urn:zitadel:iam:org:projects:roles',
    ];
    // Reserved scope: Zitadel resolves the provider from the org scope above and redirects to it
    // rather than rendering its sign-in screen. Omitted when no provider id is configured, which
    // leaves the hosted screen as the destination.
    if (options.idpId) {
      scopes.push(`urn:zitadel:iam:org:idp:id:${options.idpId}`);
    }
    const scope = scopes.join(' ');
    const searchParams = new URLSearchParams({
      client_id: zitadelConfig.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      response_mode: 'query',
      scope,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      ui_locales: options.lang,
    });
    if (options.loginHint) {
      searchParams.set('login_hint', options.loginHint);
    }

    window.location.href = `${this.fetch.backendUrl}/oauth/v2/authorize?${searchParams.toString()}`;
  }

  public async getToken(code: string, clientId: string, codeVerifier: string, redirectUri: string): Promise<ZitadelGetTokenResponse> {
    return this.fetch.get({
      url: '/oauth/v2/token',
      queryParams: {
        grant_type: 'authorization_code',
        code,
        client_id: clientId,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
      },
    });
  }

  /**
   * Ends the Zitadel session and comes back to `postLogoutRedirectUri`, which has to match one of
   * the app's registered post-logout URIs exactly. So anything the landing page needs to know
   * rides in `state`, which the logout endpoint echoes back as a query parameter, instead of being
   * appended to the URI.
   */
  public endSession(idTokenHint: string, postLogoutRedirectUri: string, state?: string | null): void {
    const searchParams = new URLSearchParams({
      id_token_hint: idTokenHint,
      post_logout_redirect_uri: postLogoutRedirectUri,
      ...(state ? {state} : {}),
    });

    window.location.href = `${this.fetch.backendUrl}/oidc/v1/end_session?${searchParams.toString()}`;
  }

  // Refresh tokens may be rotated — after a successful refresh, persist the new
  // refresh token from the response and do not reuse the old one.
  // To manually trigger a refresh, paste in Chrome DevTools console:
  //   a = JSON.parse(localStorage.getItem('auth')); a.expires_in = 0; localStorage.setItem('auth', JSON.stringify(a));
  // Then perform any action that triggers an API call.
  public async refreshToken(refreshToken: string, zitadelConfig: ZitadelConfig): Promise<ZitadelGetTokenResponse> {
    // Refresh uses a minimal scope — the org/project scopes that control the login UI
    // cause invalid_scope errors here and are not needed for token renewal.
    return this.fetch.get({
      url: '/oauth/v2/token',
      queryParams: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        scope: 'openid profile email offline_access',
        client_id: zitadelConfig.client_id,
      },
    });
  }
}
